/**
 * サイドバー・トップバーのアイコン（U6・D-82）。
 *
 * 出所＝商談デモ `assets/app.js` の `ICONS`（線画・24グリッド）を TSX 化したもの。
 * **デモのロジックは転用しない**（CLAUDE.md）が、これは視覚言語＝D-43 の「画面仕様の
 * リファレンス実装」そのもの。色は `currentColor`＝置いた場所の文字色に従う（トークン非依存）。
 *
 * ライブラリは足さない（console の初期バンドルを増やさないため＝インライン SVG で十分）。
 */

import type { ReactNode } from "react";

export type IconName = keyof typeof ICON_PATHS;

const ICON_PATHS = {
  dash: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
    </>
  ),
  inbox: (
    <>
      <path d="M3 13h5l2 3h4l2-3h5"/><path d="M5 6h14l2 7v6H3v-6z"/>
    </>
  ),
  list: (
    <>
      <path d="M8 6h13M8 12h13M8 18h13"/><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01"/>
    </>
  ),
  doc: (
    <>
      <path d="M6 2.8h8l4 4v14.4H6z"/><path d="M14 2.8v4h4"/><path d="M9 12h6M9 16h6"/>
    </>
  ),
  bill: (
    <>
      <circle cx="12" cy="12" r="9"/><path d="M8.5 8l3.5 4.5L15.5 8M12 12.5V17M9.5 13.5h5M9.5 15.5h5"/>
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.4"/><path d="M2.8 20c.7-3.4 3.2-5.2 6.2-5.2s5.5 1.8 6.2 5.2"/><circle cx="17.5" cy="9.5" r="2.6"/><path d="M16.2 14.9c2.6.2 4.4 1.8 5 4.4"/>
    </>
  ),
  box: (
    <>
      <path d="M12 2.8 21 7.5v9L12 21.2 3 16.5v-9z"/><path d="M3 7.5l9 4.7 9-4.7M12 12.2v9"/>
    </>
  ),
  stock: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 17.5h7M17.5 14v7"/>
    </>
  ),
  phone: (
    <>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5"/><path d="M10.5 18.5h3"/>
    </>
  ),
  bag: (
    <>
      <path d="M6.5 8V6.6a5.5 5.5 0 0 1 11 0V8"/><path d="M4 8h16l-1.2 12.2a1.8 1.8 0 0 1-1.8 1.6H7a1.8 1.8 0 0 1-1.8-1.6z"/>
    </>
  ),
  truck: (
    <>
      <rect x="1" y="5" width="14" height="12" rx="1.5"/><path d="M15 9h4l3 3v5h-7z"/><circle cx="6" cy="18.5" r="1.9"/><circle cx="18" cy="18.5" r="1.9"/>
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3.2"/><path d="M12 2.6v3M12 18.4v3M4.4 4.4l2.1 2.1M17.5 17.5l2.1 2.1M2.6 12h3M18.4 12h3M4.4 19.6l2.1-2.1M17.5 6.5l2.1-2.1"/>
    </>
  ),
  ticket: (
    <>
      <path d="M3 8.5A2 2 0 0 1 5 6.5h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4z"/><path d="M13 6.5v11"/>
    </>
  ),
  shield: (
    <>
      <path d="M12 2.6 20 6v6c0 5-3.5 8-8 9.4C7.5 20 4 17 4 12V6z"/><path d="M9 12l2 2 4-4"/>
    </>
  ),
  clipboard: (
    <>
      <path d="M9 4h6v3H9z"/><path d="M9 5.5H6.5v15h11v-15H15"/><path d="M9 11h6M9 15h4"/>
    </>
  ),
  tag: (
    <>
      <path d="M11 3H4v7l10 10 7-7L11 3z"/><circle cx="7.5" cy="7.5" r="1.4"/>
    </>
  ),
  building: (
    <>
      <path d="M4 21V5.5L13 3v18"/><path d="M13 9h6.5V21"/><path d="M20 21H3.5"/><path d="M7.5 8h2M7.5 12h2M7.5 16h2M16 13h1.5M16 17h1.5"/>
    </>
  ),
  link: (
    <>
      <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.6-2.6a4 4 0 0 0-5.7-5.7L11.7 6.6"/><path d="M13.5 10.5a4 4 0 0 0-5.7 0l-2.6 2.6a4 4 0 1 0 5.7 5.7l1.4-1.4"/>
    </>
  ),
} as const;

export function Icon({ name, size = 18 }: { name: IconName; size?: number }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}
